import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import logoSymbol from '../assets/logo-symbol.png';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    setResendStatus('');
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setResendStatus(data.message || 'If an account exists, a new verification email has been sent.');
    } catch {
      setResendStatus('Could not send the email right now. Please try again in a moment.');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendStatus('');
    
    try {
      const userData = await login(email, password);
      
      // Use the returned user data directly for navigation
      // Only admin users need to select a property group
      if (userData?.role === 'ADMIN') {
        navigate('/select-group');
      } else if (userData?.role === 'VENDOR') {
        navigate('/vendor-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      // More detailed error handling
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (err.response.data?.code === 'EMAIL_NOT_VERIFIED') {
          setNeedsVerification(true);
        }
        setError(err.response.data.message || 'Login failed');
      } else if (err.request) {
        // The request was made but no response was received
        setError('No response from server. Please check your network connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        setError('An unexpected error occurred');
      }
    }
  };


  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#22d3ee]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#38bdf8]/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-[#22d3ee]/5 to-[#38bdf8]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 glass-card p-8 w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img
            src={logoSymbol}
            alt="BlockBoard"
            className="w-16 h-16 mx-auto mb-4 object-contain drop-shadow-[0_0_12px_rgba(56,189,248,0.35)]"
          />
          <h1 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-white/80 bg-clip-text">
            Welcome to BlockBoard
          </h1>
          <p className="text-white/60">Sign in to your property management portal</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 backdrop-blur-sm">
            {error}
            {needsVerification && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="text-sm text-[#22d3ee] hover:text-[#38bdf8] underline transition-colors duration-200"
                >
                  Resend verification email
                </button>
                {resendStatus && (
                  <p className="text-white/70 text-sm mt-2">{resendStatus}</p>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-white/80">
              Email or Username
            </label>
            <input
              type="text"
              id="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your email or username"
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-white/80">
              Password
            </label>
            <input
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field w-full"
              placeholder="Enter your password"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-[#22d3ee] hover:text-[#38bdf8] transition-colors duration-200"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            className="btn-primary w-full"
          >
            Sign In
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white/40 text-xs">
            Secure • Modern • Efficient
          </p>
        </div>

        {/* Back to main site */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white text-sm transition duration-200"
          >
            ← Back to main site
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login; 